import {
  type ContractDetail,
  type PaymentInvoiceLineInput,
  type PaymentRequestView,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { prKey } from './paymentParts';

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));
const fmt = (n: number | null) => (n === null ? '—' : String(Math.round(n * 1e5) / 1e5).replace('.', ','));

interface Row {
  name: string;
  unit: string;
  quantity: string;
  price: string;
  contractItemId: string | null;
}
const fromView = (pr: PaymentRequestView): Row[] =>
  pr.invoiceLines.map((l) => ({
    name: l.name,
    unit: l.unit,
    quantity: String(l.quantity),
    price: l.price === null ? '' : String(l.price),
    contractItemId: l.contractItemId,
  }));

/**
 * Сверка счёта поставщика с договором и приходом на склад (ФН-20): позиции счёта, результат по каждой
 * позиции и причина остановки. Остановку по расхождению снимает допсоглашение или исправление прихода:
 * после них — «Сверить заново».
 */
export function InvoiceMatchPanel({
  pr,
  canEdit,
}: {
  pr: PaymentRequestView;
  /** Тот, кто готовит заявки на оплату */
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const editable = canEdit && money && ['draft', 'approval', 'hold'].includes(pr.status);
  const [rows, setRows] = useState<Row[] | null>(null);
  const contract = useQuery({
    queryKey: ['contracts', 'detail', pr.contractId],
    queryFn: () => api<ContractDetail>(`/contracts/${pr.contractId}`),
    enabled: Boolean(pr.contractId) && editable,
  });
  const refresh = (v: PaymentRequestView) => {
    qc.setQueryData(prKey(v.id), v);
    for (const k of ['procurement', 'finance', 'payments']) qc.invalidateQueries({ queryKey: [k] });
  };
  const save = useMutation({
    mutationFn: (lines: PaymentInvoiceLineInput[]) =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}/invoice`, {
        method: 'PUT',
        body: { lines },
      }),
    onSuccess: (v) => {
      refresh(v);
      setRows(null);
      toast('Позиции счёта сохранены, сверка выполнена');
    },
  });
  const recheck = useMutation({
    mutationFn: () =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}/action`, {
        body: { action: 'recheck' },
      }),
    onSuccess: (v) => {
      refresh(v);
      toast(v.match?.status === 'ok' ? 'Сверка прошла' : 'Расхождение осталось');
    },
  });
  const m = pr.match;
  // Нечего сверять: нет договора со спецификацией, не поставка или сверка выключена
  if (!pr.contractId) return null;
  if (m?.status === 'not_required' && !pr.invoiceLines.length)
    return (
      <p className="text-[13px] text-ink-2">
        Сверка счёта с договором и приходом не нужна: {m.note?.toLowerCase()}.
      </p>
    );
  const cur = rows ?? fromView(pr);
  const set = (i: number, patch: Partial<Row>) =>
    setRows(cur.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const specRows = contract.data?.items ?? [];
  const parsed = cur.map((r) => ({ ...r, q: num(r.quantity), p: num(r.price) }));
  const valid =
    parsed.length > 0 &&
    parsed.every((r) => r.name.trim() && r.unit.trim() && r.q > 0 && Number.isFinite(r.p) && r.p >= 0);
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-4" aria-label="Сверка счёта">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold">Сверка счёта с договором и приходом</h3>
        {m ? (
          <span
            className={clsx(
              'rounded-md px-2 py-1 text-[12px] font-medium',
              m.status === 'ok' && 'bg-success-soft text-ink',
              m.status === 'mismatch' && 'bg-danger-soft text-ink',
              m.status === 'not_required' && 'bg-tile text-ink-2',
            )}
          >
            {m.status === 'ok' ? 'Сходится' : m.status === 'mismatch' ? 'Не сходится' : 'Не нужна'}
          </span>
        ) : null}
      </div>
      {m?.status === 'mismatch' ? (
        <ul className="list-disc pl-5 text-[13px] text-ink" aria-label="Расхождения">
          {m.issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      ) : null}
      {m?.rows.length ? (
        <div className="flex flex-col gap-2">
          {m.rows.map((r) => (
            <div
              key={`${r.name}|${r.unit}`}
              className={clsx(
                'rounded-md border px-3 py-2 text-[13px]',
                r.issues.length ? 'border-danger/50 bg-danger-soft' : 'border-line bg-surface',
              )}
            >
              <div className="font-medium">
                {r.name}, {r.unit}
              </div>
              <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-ink-2 sm:grid-cols-4">
                <div>
                  <dt className="text-[12px]">В счёте</dt>
                  <dd className="text-ink">
                    {fmt(r.invoiceQty)}
                    {r.invoicePrice !== null ? ` × ${tenge(r.invoicePrice)}` : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-[12px]">По договору</dt>
                  <dd className="text-ink">
                    {fmt(r.contractQty)}
                    {r.contractPrice !== null ? ` × ${tenge(r.contractPrice)}` : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-[12px]">Уже выставлено</dt>
                  <dd className="text-ink">{fmt(r.invoicedBefore)}</dd>
                </div>
                <div>
                  <dt className="text-[12px]">Принято</dt>
                  <dd className="text-ink">
                    {r.receivedQty === null ? 'не нужно (аванс)' : fmt(r.receivedQty)}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
          <p className="text-[12px] text-ink-2">
            Допуск: цена {m.pricePct} %, количество {m.qtyPct} % (задаёт администратор).
            {m.invoiceTotal !== null ? ` Сумма счёта ${tenge(m.invoiceTotal)}.` : ''}
          </p>
        </div>
      ) : null}
      {pr.status === 'hold' && pr.hold?.kinds.includes('match') && canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" loading={recheck.isPending} onClick={() => recheck.mutate()}>
            Сверить заново
          </Button>
          <span className="text-[13px] text-ink-2">
            Расхождение снимает допсоглашение (ДГ-08), исправление прихода на складе или принятие объёмов
            площадки; решением финансового менеджера оно не снимается.
          </span>
        </div>
      ) : null}
      {editable ? (
        <div className="flex flex-col gap-2">
          <h4 className="text-[13px] font-semibold">Позиции счёта или ЭСФ поставщика</h4>
          {cur.map((r, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: строки формы без собственного ключа
              key={i}
              className="grid grid-cols-2 gap-2 rounded-md border border-line p-2 sm:grid-cols-[1fr_5rem_6rem_8rem_auto]"
            >
              <Input
                aria-label="Наименование"
                placeholder="Наименование"
                className="col-span-2 sm:col-span-1"
                value={r.name}
                onChange={(e) => set(i, { name: e.target.value, contractItemId: null })}
              />
              <Input
                aria-label="Единица"
                placeholder="Ед."
                value={r.unit}
                onChange={(e) => set(i, { unit: e.target.value })}
              />
              <Input
                aria-label="Количество"
                placeholder="Кол-во"
                inputMode="decimal"
                value={r.quantity}
                onChange={(e) => set(i, { quantity: e.target.value })}
              />
              <Input
                aria-label="Цена за единицу"
                placeholder="Цена"
                inputMode="decimal"
                value={r.price}
                onChange={(e) => set(i, { price: e.target.value })}
              />
              <Button
                variant="ghost"
                aria-label="Убрать строку"
                onClick={() => setRows(cur.filter((_, k) => k !== i))}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() =>
                setRows([...cur, { name: '', unit: '', quantity: '', price: '', contractItemId: null }])
              }
            >
              Добавить позицию
            </Button>
            {specRows.length ? (
              <Button
                onClick={() =>
                  setRows(
                    specRows.map((s) => ({
                      name: s.name,
                      unit: s.unit,
                      quantity: String(s.quantity),
                      price: String(s.price ?? ''),
                      contractItemId: s.id,
                    })),
                  )
                }
              >
                Взять позиции спецификации
              </Button>
            ) : null}
            <Button
              variant="primary"
              disabled={!valid}
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  parsed.map((r) => ({
                    name: r.name.trim(),
                    unit: r.unit.trim(),
                    quantity: r.q,
                    price: r.p,
                    contractItemId: r.contractItemId,
                  })),
                )
              }
            >
              Сохранить и сверить
            </Button>
          </div>
          <ErrorBox error={save.error ?? recheck.error} />
        </div>
      ) : null}
    </section>
  );
}
